import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Shield, Star, Sparkles, BadgeCheck, Palette, Droplets, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";

export const VehicleWrapping = () => {
  // Scroll to top when page loads and set page title
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Vehicle Wrapping | Mini Stripes UK";
  }, []);

  return (
    <div className="pb-16" data-testid="vehicle-wrapping-page">
      {/* Hero Section with Background Image */}
      <div className="relative min-h-[70vh] flex items-start justify-center overflow-hidden">
        {/* Background Car Image */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_b4bd2a3d-2b21-431c-afa7-18a2028f4403/artifacts/m7s1bw2p_hyundai%20wrap.png')`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            transform: 'scale(1.0)'
          }}
        />
        
        {/* Content */}
        <div className="relative z-10 text-center px-4 md:px-8 max-w-4xl mx-auto pt-28">
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Main Content Section */}
        <div className="mb-12 -mt-32 relative z-20">
          <div className="bg-gray-100/60 backdrop-blur-sm rounded-3xl p-8 md:p-10" data-testid="wrapping-content-section">
            <h1 
              className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-8 text-center text-black"
            >
              Premium Vehicle Wrapping Service
            </h1>
            <div className="space-y-6 max-w-3xl mx-auto mb-10">
              <p 
                className="text-center text-gray-800 text-lg leading-relaxed font-medium"
              >
                We provide a premium vehicle wrapping service tailored for customers who expect exceptional quality and craftsmanship. Our wraps are supplied and fitted in a fully air‑conditioned, meticulously maintained work bay designed to deliver flawless, high‑end results.
              </p>
              <p 
                className="text-center text-gray-800 text-lg leading-relaxed font-medium"
              >
                As experienced specialists and certified trainers in Hexis wrap films, we bring industry‑leading expertise to every project. Our range includes an extensive selection of luxury colours and finishes from all top wrap brands, with a curated collection available to view here:{" "}
                <Link to="/products/wrap-vinyl" className="text-black underline hover:no-underline font-bold">
                  View Our Wrap Vinyl Collection
                </Link>.
              </p>
              <p 
                className="text-center text-gray-800 text-lg leading-relaxed font-medium"
              >
                Whether you're seeking a sophisticated colour change or a bespoke finish, we offer a seamless, professional experience from consultation to completion. Appointments and quotations are available upon request.
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              <div className="flex flex-col items-center text-center p-6 bg-white rounded-2xl shadow-md border border-gray-200">
                <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mb-4">
                  <Palette className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-2 text-black">
                  Luxury Colours
                </h3>
                <p className="text-sm text-gray-700 font-medium">
                  Extensive selection of premium finishes from top brands
                </p>
              </div>

              <div className="flex flex-col items-center text-center p-6 bg-white rounded-2xl shadow-md border border-gray-200">
                <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mb-4">
                  <Droplets className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-2 text-black">
                  Climate Controlled
                </h3>
                <p className="text-sm text-gray-700 font-medium">
                  Air-conditioned work bay for flawless application
                </p>
              </div>

              <div className="flex flex-col items-center text-center p-6 bg-white rounded-2xl shadow-md border border-gray-200">
                <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mb-4">
                  <Layers className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-2 text-black">
                  Bespoke Finishes
                </h3>
                <p className="text-sm text-gray-700 font-medium">
                  Custom solutions tailored to your vision
                </p>
              </div>
            </div>

            {/* Credentials */}
            <div className="flex items-center justify-center gap-8 mb-10 flex-wrap">
              <div className="flex items-center gap-2">
                <BadgeCheck className="h-6 w-6 text-black" />
                <span className="text-black font-semibold">Hexis Certified</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="h-6 w-6 text-black" />
                <span className="text-black font-semibold">Premium Finish</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-6 w-6 text-black" />
                <span className="text-black font-semibold">Expert Fitting</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-4 flex-wrap">
              <Link to="/contact">
                <Button 
                  className="rounded-full px-10 py-4 text-lg font-semibold"
                  data-testid="contact-quote-btn"
                >
                  Contact Us for a Quote
                </Button>
              </Link>
              <Link to="/products/wrap-vinyl">
                <Button 
                  className="rounded-full px-10 py-4 text-lg font-medium"
                >
                  View the Wrap Vinyl
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Swatch Image - Full Width */}
      <div 
        className="w-full py-20 -mt-32"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_b4bd2a3d-2b21-431c-afa7-18a2028f4403/artifacts/se6fk64d_swatch.png')`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: 'white',
          minHeight: '600px'
        }}
      >
      </div>
    </div>
  );
};

export default VehicleWrapping;
